import * as THREE from 'three'

/**
 * 收集导入模型 renderable 已有的 Three.js 资源引用。
 *
 * 该类只读取 Mesh/InstancedMesh 当前持有的对象引用，不 clone、不替换 geometry/material，
 * 因而不会改变完整 GLB 的天然实例化与几何复用关系。
 */
export class ImportedModelRenderableResourceCollector {
  /**
   * 收集一个 node lease 需要持有的资源。
   *
   * @param meshes 已由 complete 分析器按最近业务 occurrence 划分的 mesh。
   * @param objectRoots 当前 node 负责生命周期的 Object3D 根。
   * @returns 按对象引用去重的资源集合。
   */
  public collect(
    meshes: readonly THREE.Mesh[],
    objectRoots: readonly THREE.Object3D[],
  ): PuppyCAD.ImportedModelResourceSet {
    const geometries = new Set<THREE.BufferGeometry>()
    const materials = new Set<THREE.Material>()
    const textures = new Set<THREE.Texture>()

    meshes.forEach((mesh) => {
      if (mesh.geometry) {
        geometries.add(mesh.geometry)
      }
      const meshMaterials = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
      meshMaterials.forEach((material) => {
        if (!material) {
          return
        }
        materials.add(material)
        this.collectMaterialTextures(material, textures)
      })
    })

    return Object.freeze({
      objectRoots: new Set(objectRoots),
      geometries,
      materials,
      textures,
    })
  }

  /**
   * 收集材质属性直接引用的纹理。
   *
   * @param material 当前 Mesh 使用的材质。
   * @param textures 输出纹理集合。
   */
  private collectMaterialTextures(
    material: THREE.Material,
    textures: Set<THREE.Texture>,
  ): void {
    Object.values(material as THREE.Material & Record<string, unknown>).forEach((value) => {
      if (value instanceof THREE.Texture) {
        textures.add(value)
      }
    })
  }
}
